import 'react-inner-image-zoom/lib/InnerImageZoom/styles.min.css'

import InnerImageZoom from 'react-inner-image-zoom'
import { SLUG, STRUMENTI } from 'utils/endpoint'
import { MEMBER, api, getTipoStrumento, getImage } from 'utils'
import React, { useEffect, useMemo, useState } from 'react'
import Breadcrumbs from 'components/atomic/Breadcrumbs'
import Credits from 'components/atomic/Credits'
import Error from 'next/error'
import MetaSeo from 'components/atomic/MetaSeo'
import StrumentoSummary from 'components/StrumentoSummary'
import { sendGTMData } from 'utils/gtm.js'
import Head from 'next/head'
import Ioplus from 'components/atomic/Ioplus'
import { Bar, Correlati, StickyActions } from 'react-components/organisms'
import { Zoom } from 'react-components/atoms/Icons'
import { useTrans } from 'hooks'
import { useDispatch } from 'react-redux'
import { setItem } from 'containers/toolbar'

const StrumentoDetail = ({ item, page, statusCode, lezioni, articoli, strumenti }) => {
  if (!item) return <Error statusCode={statusCode} />

  const t = useTrans()

  const dispatch = useDispatch()

  const [strumento, setStrumento] = useState(item)

  const refresh = async () => {
    const { data } = await api.get(`${STRUMENTI}${strumento.id}/`)
    setStrumento(data)
    dispatch(setItem(data))
  }

  useEffect(() => {
    refresh()
  }, [])

  const imgHost = process.env.NODE_ENV == 'development' ? 'http://localhost:9999' : ''

  const mainImage = getImage(strumento.main_image)

  const zoomImgSrc = strumento.extra?.zoom_image
  const zoomImage = `${imgHost}${zoomImgSrc}`

  const tipoStrumento = useMemo(() => getTipoStrumento(strumento), [strumento])

  const getLabelGTM = () => {
    let eventLabel = tipoStrumento.split(' ')[0]
    if (eventLabel == 'scheda') {
      eventLabel = 'scheda_didattica'
    }
    return eventLabel
  }

  const handleGTM = () => {
    sendGTMData({
      event: 'GAevent',
      eventID: '15',
      eventCategory: 'interazione_contenuti',
      eventAction: 'listen',
      eventLabel: getLabelGTM(),
      moreInfo: strumento.titolo.toGTMFormat(),
    })
  }

  return (
    <>
      {strumento ? (
        <>
          {mainImage && (
            <Head>
              <link rel="preload" href={mainImage} as="image" />
            </Head>
          )}

          <MetaSeo seo={page.seo} />

          <main className="strumento-page">
            <div className="w-container">
              <Breadcrumbs breadcrumbs={page.breadcrumbs} />
            </div>

            <div className="w-container-inner">
              <div className="row">
                <div className="col-lg-5">
                  {tipoStrumento.split(' ')[0] === 'audio' ? (
                    <div className="strumento-page__header-audio-box">
                      <audio controls onPlay={handleGTM} src={strumento.extra.file_url || '#'}>
                        {t('Il tuo browser non supporta elementi audio')}
                      </audio>
                    </div>
                  ) : mainImage ? (
                    <div className="strumento-page__header-image-box">
                      <InnerImageZoom
                        src={mainImage}
                        zoomSrc={zoomImage}
                        hideHint
                        fullscreenOnMobile
                        alt={strumento.titolo?.toAltTitle()}
                        title={strumento.titolo?.toAltTitle()}
                      />
                      {strumento.membership == MEMBER ? <Ioplus /> : null}
                      <Zoom />
                    </div>
                  ) : null}
                </div>
                <div className="col-lg-7" style={{ display: 'flex' }}>
                  <StrumentoSummary strumento={strumento} classes="strumento-summary" />
                  <StickyActions item={strumento} labelGTM={getLabelGTM()} save share print />
                </div>
              </div>
            </div>
            <Correlati articoli={articoli} strumenti={strumenti} lezioni={lezioni} />
            {strumento.crediti && (
              <div className="w-container-sportelli">
                <Credits crediti={strumento.crediti} />
              </div>
            )}
          </main>

          <Bar item={strumento} save share print labelGTM={getLabelGTM()} />
        </>
      ) : null}
    </>
  )
}

export async function getStaticPaths() {
  const response = await api.get(STRUMENTI)
  const strumenti = response.data.results

  const paths = strumenti
    .map((s) => {
      return { params: { slug: s.slug } }
    })
    .slice(0, 1)

  return {
    paths,
    fallback: 'blocking',
  }
}

export const getStaticProps = async ({ params: { slug }, preview, previewData }) => {
  const headers = preview && previewData?.headers ? { cookie: previewData.headers } : {}

  const response = await api.get(`${STRUMENTI}${SLUG}${slug}/`, {
    params: { server: true, preview },
    headers,
  })
  const strumento = response.data

  if (!strumento) {
    return {
      notFound: true,
    }
  }

  const page = strumento.pagina

  const toolbarProps = { toolbarItem: strumento, toolbarPage: page }

  const urlCorrelati = `${STRUMENTI}${strumento.id}/risorse_correlate/`
  const lezioniResponse = await api.get(urlCorrelati, {
    params: { limit: 3, excluded: 'raccolta/strumento/articolo' },
  })
  const lezioni = lezioniResponse.data.results

  const articoliResponse = await api.get(urlCorrelati, {
    params: { limit: 3, excluded: 'raccolta/strumento/lezione' },
  })
  const articoli = articoliResponse.data.results

  const strumentiResponse = await api.get(urlCorrelati, {
    params: { limit: 3, excluded: 'raccolta/lezione/articolo' },
  })
  const strumenti = strumentiResponse.data.results

  return {
    props: {
      item: strumento,
      page,
      key: strumento.id,
      ...toolbarProps,
      lezioni,
      articoli,
      strumenti,
      revalidate: 60 * 10, // trovare alternativa in stile data-fetching-wrapper di Luca D. per altre pagine senza dover passare manualmente il revalidate
    },
    revalidate: 60 * 10,
  }
}

export default StrumentoDetail
